import { useMemo, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ImageDropzone from "@/components/base/ImageDropzone";
import Select from "@/components/base/Select";
import { useToast } from "@/components/base/Toast";
import { useStore } from "@/context/StoreContext";
import type { MenuProduct } from "@/types";

const VAT_RATE = 0.15;

interface FormState {
  name: string;
  categoryId: string;
  price: string;
  stock: string;
  description: string;
  isAvailable: boolean;
  image: string;
}

const DEFAULT_IMAGE =
  "https://readdy.ai/api/search-image?query=Overhead%20food%20photography%20of%20a%20freshly%20cooked%20Saudi%20main%20dish%20plate%20on%20a%20clean%20light%20beige%20background%2C%20warm%20studio%20lighting%2C%20minimal%20styling%2C%20soft%20shadows&width=400&height=400&seq=yc-food-default-dish-01&orientation=squarish";

export default function ProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { show } = useToast();
  const { products, categories, saveProduct } = useStore();

  const isEdit = Boolean(id);
  const editing = isEdit
    ? (products.find((product) => product.id === id) ?? null)
    : null;

  const [form, setForm] = useState<FormState>(() =>
    editing
      ? {
          name: editing.name,
          categoryId: editing.categoryId,
          price: String(editing.price),
          stock: String(editing.stock),
          description: editing.description,
          isAvailable: editing.isAvailable,
          image: editing.image,
        }
      : {
          name: "",
          categoryId: categories[0]?.id ?? "",
          price: "",
          stock: "",
          description: "",
          isAvailable: true,
          image: "",
        },
  );
  const [error, setError] = useState("");

  const priceValue = Number(form.price);
  const vatBreakdown = useMemo(() => {
    if (!priceValue || priceValue <= 0) return { net: 0, vat: 0 };
    const net = priceValue / (1 + VAT_RATE);
    const vat = priceValue - net;
    return {
      net: Math.round(net * 100) / 100,
      vat: Math.round(vat * 100) / 100,
    };
  }, [priceValue]);

  const stockValue = form.stock.trim() === "" ? null : Number(form.stock);
  const autoOutOfStock = stockValue !== null && stockValue <= 0;

  if (isEdit && !editing) {
    return (
      <div className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card px-8 py-20 text-center">
        <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-restaurant-2-line text-3xl"></i>
        </div>
        <h2 className="text-lg font-bold text-foreground-950">
          المنتج غير موجود
        </h2>
        <p className="mt-1 text-sm text-foreground-500">
          تعذّر العثور على المنتج المطلوب، ربما تم حذفه.
        </p>
        <button
          type="button"
          onClick={() => navigate("/menu")}
          className="mt-5 inline-flex items-center gap-2 rounded-btn bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line"></i>
          العودة للمنيو
        </button>
      </div>
    );
  }

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("الرجاء اختيار صورة صحيحة (JPG أو PNG)");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setError("حجم صورة المنتج أكبر من 3 ميجابايت");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, image: String(reader.result ?? "") }));
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!form.image) {
      setError("الرجاء إضافة صورة المنتج");
      return;
    }
    if (!form.name.trim()) {
      setError("الرجاء إدخال اسم المنتج");
      return;
    }
    if (!form.description.trim()) {
      setError("الرجاء إدخال وصف المنتج");
      return;
    }
    if (!form.categoryId) {
      setError("الرجاء اختيار التصنيف");
      return;
    }
    if (!form.price || Number(form.price) <= 0) {
      setError("الرجاء إدخال سعر صحيح أكبر من صفر");
      return;
    }
    saveProduct({
      id: editing?.id ?? `p${Date.now()}`,
      name: form.name.trim(),
      categoryId: form.categoryId,
      price: Number(form.price),
      stock: stockValue ?? 0,
      description: form.description.trim(),
      isAvailable: form.isAvailable,
      image: form.image,
    });
    show(editing ? "تم حفظ تعديلات المنتج" : "تمت إضافة المنتج للمنيو");
    navigate("/menu");
  };

  return (
    <div className="space-y-4">
      <div className="yc-fade-up flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/menu")}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
          aria-label="رجوع"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>
        <div>
          <h2 className="text-xl font-bold text-foreground-950">
            {editing ? "تعديل المنتج" : "إضافة منتج جديد"}
          </h2>
          <p className="mt-1 text-sm text-foreground-500">
            {editing
              ? "عدّل بيانات المنتج ثم احفظ التغييرات"
              : "أدخل بيانات المنتج لإضافته للمنيو"}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-5 md:p-6"
      >
        <div className="max-w-2xl space-y-5">
          <ImageDropzone
            label="صورة المنتج"
            required
            previewSrc={form.image}
            hint="صورة واضحة بخلفية بسيطة — JPG أو PNG بحد أقصى 3 ميجابايت"
            onFile={handleFile}
          />

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              اسم المنتج <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(event) =>
                setForm({ ...form, name: event.target.value })
              }
              placeholder="مثال: كبسة دجاج"
              className="w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              الوصف <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              maxLength={200}
              onChange={(event) =>
                setForm({ ...form, description: event.target.value })
              }
              rows={3}
              placeholder="وصف مختصر للمنتج ومكوناته"
              className="w-full rounded-btn border border-background-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-300 resize-none"
            ></textarea>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                التصنيف <span className="text-red-500">*</span>
              </label>
              <Select
                value={form.categoryId}
                options={categories.map((category) => ({
                  value: category.id,
                  label: `${category.name}${category.isActive ? "" : " (معطّل)"}`,
                }))}
                onChange={(value) => setForm({ ...form, categoryId: value })}
                icon="ri-price-tag-3-line"
                ariaLabel="التصنيف"
              />
              <p className="mt-1.5 text-xs text-foreground-500">
                يمكنك نقل المنتج بين التصنيفات في أي وقت.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                السعر شامل الضريبة (ر.س) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={form.price}
                onChange={(event) =>
                  setForm({ ...form, price: event.target.value })
                }
                placeholder="45"
                className="w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300"
              />
              {priceValue > 0 && (
                <p className="mt-1.5 text-xs text-foreground-500">
                  قبل الضريبة {vatBreakdown.net} ر.س — الضريبة (15%){" "}
                  {vatBreakdown.vat} ر.س
                </p>
              )}
            </div>
          </div>

          <div className="rounded-btn bg-accent-50 border border-accent-200 px-4 py-3 text-xs leading-6 text-accent-900">
            السعر المدخل شامل ضريبة القيمة المضافة، ويستخرج النظام الضريبة منه
            تلقائيًا (15%) عند إصدار الفاتورة.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                الكمية المتاحة (اختياري)
              </label>
              <input
                type="number"
                min="0"
                value={form.stock}
                onChange={(event) =>
                  setForm({ ...form, stock: event.target.value })
                }
                placeholder="اترك الحقل فارغًا لعدم تحديد كمية"
                className="w-full h-11 rounded-btn border border-background-200 px-3 text-sm outline-none focus:ring-2 focus:ring-primary-300"
              />
              {autoOutOfStock && (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-500">
                  <i className="ri-error-warning-line"></i>
                  الكمية صفر — سيظهر المنتج «غير متوفر» تلقائيًا.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-2">
                حالة المنتج
              </label>
              <button
                type="button"
                role="switch"
                aria-checked={form.isAvailable}
                onClick={() =>
                  setForm({ ...form, isAvailable: !form.isAvailable })
                }
                className="w-full flex items-center justify-between rounded-btn border border-background-200 px-4 h-11 cursor-pointer"
              >
                <span className="text-sm font-medium text-foreground-800">
                  {form.isAvailable ? "مفعّل للطلب" : "موقوف مؤقتًا"}
                </span>
                <span
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                    form.isAvailable ? "bg-primary-500" : "bg-background-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all duration-200 ${
                      form.isAvailable ? "start-5" : "start-0.5"
                    }`}
                  ></span>
                </span>
              </button>
            </div>
          </div>

          {error && (
            <p className="flex items-center gap-2 text-xs font-medium text-red-500">
              <i className="ri-error-warning-line"></i>
              {error}
            </p>
          )}
        </div>

        <div className="mt-6 flex gap-3 border-t border-background-100 pt-5">
          <button
            type="button"
            onClick={() => navigate("/menu")}
            className="h-11 px-6 rounded-btn bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            إلغاء
          </button>
          <button
            type="submit"
            className="h-11 px-6 rounded-btn bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {editing ? "حفظ التعديلات" : "إضافة المنتج"}
          </button>
        </div>
      </form>
    </div>
  );
}